'use client';

import { useEffect, useState } from 'react';
import {
  blobPath,
  blobSvg,
  wavePath,
  waveSvg,
  type SvgGenVariant,
} from '@/lib/browser-svggen-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

function randomValues(count: number): number[] {
  const array = new Uint32Array(count);
  crypto.getRandomValues(array);
  return Array.from(array, (n) => n / 0xffffffff);
}

export default function BrowserSvgGenerator({ variant }: { variant: SvgGenVariant }) {
  const isBlob = variant === 'svg-blob-generator';
  const [points, setPoints] = useState(isBlob ? 6 : 5);
  const [color, setColor] = useState('#6366f1');
  const [contrast, setContrast] = useState(isBlob ? 0.5 : 0.6);
  const [values, setValues] = useState<number[]>([]);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  function regenerate(n: number) {
    setCopyMessage(null);
    setValues(randomValues(Math.max(3, Math.floor(n))));
  }

  useEffect(() => {
    regenerate(points);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const path = values.length
    ? isBlob
      ? blobPath(values, 500, contrast)
      : wavePath(values, 1440, 320, contrast)
    : '';
  const svg = path ? (isBlob ? blobSvg(path, color) : waveSvg(path, color)) : '';
  const viewBox = isBlob ? '0 0 500 500' : '0 0 1440 320';

  async function handleCopy() {
    try {
      await copyText(svg);
      setCopyMessage('SVG copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">SVG</span>
        <h2 className="tool-live-title">
          {isBlob ? 'Generate an SVG blob' : 'Generate an SVG wave'}
        </h2>
        <p className="tool-live-subtitle">
          Adjust the controls, generate a shape, and copy the SVG. Everything runs in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>{isBlob ? 'Points' : 'Peaks'}</span>
              <input
                max={12}
                min={3}
                onChange={(event) => {
                  const n = Number(event.target.value);
                  setPoints(n);
                  regenerate(n);
                }}
                type="range"
                value={points}
              />
            </label>
            <label className="tool-live-field">
              <span>Contrast</span>
              <input
                max={1}
                min={0.1}
                onChange={(event) => setContrast(Number(event.target.value))}
                step={0.05}
                type="range"
                value={contrast}
              />
            </label>
            <label className="tool-live-field">
              <span>Color</span>
              <input
                onChange={(event) => setColor(event.target.value)}
                type="color"
                value={color}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => regenerate(points)}
              type="button"
            >
              Generate new
            </button>
            {svg ? (
              <button className="button button-secondary" onClick={handleCopy} type="button">
                Copy SVG
              </button>
            ) : null}
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            className="tool-live-preview-frame"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          >
            {path ? (
              <svg
                preserveAspectRatio="xMidYMid meet"
                style={{ width: '100%', maxHeight: 260 }}
                viewBox={viewBox}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d={path} fill={color} />
              </svg>
            ) : null}
          </div>
          <label className="tool-live-field tool-live-field-full">
            <span>SVG code</span>
            <textarea readOnly rows={4} value={svg} />
          </label>
          <p className="tool-live-panel-note">Generated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
